/**
 * Derives SearchableCard field options from cached test location details.
 *
 * @module useSiteDetailsFieldOptions
 */
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { TestLocationDetail } from "../../../../api/services/siteNpiDetails/siteNpiDetailsTypes";
import { RootState } from "../../../../redux/store/store";
import {
  buildAddressFieldOptionsForNpi,
  buildSiteDetailsFieldOptions,
} from "../siteDetailsFieldOptionsHelper";
import {
  SiteDetailsFieldOptions,
  SITE_TYPE_OPTIONS,
} from "../siteDetailsForm.config";

/**
 * Memoised per-field SearchableCard options sourced from `testLocationDetails`.
 *
 * Alias, NPI, and Name options are the distinct values across every cached
 * location. Address, city, state, and zip options are scoped to the
 * currently selected NPI and come from each matching row's `address_details`.
 * Type options are the fixed Internal and External choices.
 *
 * @param selectedNpi - NPI currently selected in the form.
 * @returns Field option map and slice loading flag for the Site NPI modal.
 */
export const useSiteDetailsFieldOptions = (
  selectedNpi: string,
): {
  fieldOptions: SiteDetailsFieldOptions;
  locations: TestLocationDetail[];
  loading: boolean;
} => {
  const { locations, loading } = useSelector(
    (state: RootState) => state.testLocationDetails,
  );

  const fieldOptions = useMemo(
    (): SiteDetailsFieldOptions => ({
      ...buildSiteDetailsFieldOptions(locations),
      ...buildAddressFieldOptionsForNpi(selectedNpi, locations),
      type: SITE_TYPE_OPTIONS,
    }),
    [locations, selectedNpi],
  );

  return { fieldOptions, locations, loading };
};
